import { RoomProvider } from "@liveblocks/react";
import { useAtom } from "jotai";
import { useUpdateAtom } from "jotai/utils";
import { useRouter } from "next/dist/client/router";
import Link from "next/link";
import React, { useEffect, useState } from "react";
import Presence from "../av/Presence";
import { InlineButton } from "../components/Button";
import { DialogTakeover, Message } from "../components/Dialog";
import Loader from "../components/Loader";
import {
  blocksRefAtom,
  currentSessionIdAtom,
  currentSpaceAtom,
  currentUserAtom,
  selectionAtom,
} from "../jotai/atoms";
import { DEFAULT_PRESENCE } from "../liveblocks/presences";
import { styled } from "../stitches.config";
import centerAt from "../utils/centerAt";
import { gridToPixelPosition, gridToPixelSize } from "../utils/gridPosition";
import { createSessionId } from "../utils/session";
import ArchivedLoader from "./loaders/ArchivedLoader";
import SpaceCreationLoader from "./loaders/SpaceCreationLoader";
import { InitParamLoader } from "./InitParamLoader";
import { CanvasGrid } from "./Space";

export default function SpaceLoader({
  children,
}: {
  children: React.ReactNode;
}) {
  const [currentSpace, error] = useLoadSpace();

  if (error) {
    return (
      <ErrorMessage>
        Invalid space ID. Space ID can only contains lowercase alphabets,
        numbers, underscores (_), and hyphens (-). Please try{" "}
        <StyledLink href="/">creating a new one</StyledLink>
      </ErrorMessage>
    );
  }

  if (!currentSpace) {
    return <CanvasGrid className="background" />;
  }

  return (
    <RoomProvider
      key={currentSpace.id}
      id={currentSpace.id}
      defaultPresence={() => DEFAULT_PRESENCE}
    >
      {/*  showing the canvas grid while loading spacing */}
      <CanvasGrid className="background" />
      <SpaceCreationLoader>
        <ArchivedLoader>
          <InitParamLoader>
            {children}
            <ScrollToBlock />
            <Presence />
          </InitParamLoader>
        </ArchivedLoader>
      </SpaceCreationLoader>
    </RoomProvider>
  );
}

function useLoadSpace() {
  const [currentSpace, setCurrentSpace] = useAtom(currentSpaceAtom);
  const setCurrentSessionId = useUpdateAtom(currentSessionIdAtom);
  const [currentUser] = useAtom(currentUserAtom);
  const [error, setError] = useState<boolean>(false);
  const { isReady, query } = useRouter();
  const { spaceId } = query;

  useEffect(() => {
    if (!isReady) {
      return;
    }

    const valid =
      currentUser &&
      typeof spaceId === "string" &&
      spaceId.match(/^[a-zA-Z0-9_-]+$/); //only allow alphanumeric space name

    if (!valid) {
      setError(true);
      return;
    }

    if (currentSpace?.id !== spaceId) {
      setCurrentSpace({ id: spaceId });
      setCurrentSessionId(createSessionId(currentUser.id));
    }
  }, [
    isReady,
    spaceId,
    currentSpace,
    setCurrentSpace,
    currentUser,
    setCurrentSessionId,
  ]);

  return [currentSpace, error] as [typeof currentSpace, typeof error];
}

const StyledLink = styled(Link, {
  color: "$linkBlue",
  textDecoration: "none",
});

function ErrorMessage({ children }: { children: React.ReactNode }) {
  return (
    <DialogTakeover title="Oops!">
      <Message>
        {children || (
          <>
            An unknown error has occurred. Please report a bug (bottom left),
            and then try{" "}
            <InlineButton
              aria-label="reload"
              onClick={() => window.location.reload()}
            >
              reloading this space
            </InlineButton>
          </>
        )}
      </Message>
    </DialogTakeover>
  );
}

function ScrollToBlock() {
  const router = useRouter();
  const [blocksRef] = useAtom(blocksRefAtom);
  const setSelection = useUpdateAtom(selectionAtom);
  const { bid } = router.query;

  useEffect(() => {
    if (typeof bid !== "string") return;
    const block = blocksRef.current[bid];
    if (block) {
      const pos = gridToPixelPosition(block.position);
      const size = gridToPixelSize(block.size);
      const center = {
        x: pos.x + 0.5 * size.width,
        y: pos.y + 0.5 * size.height,
      };

      centerAt(center);
      setSelection({
        [block.id]: Date.now(),
      });
    }
  }, [bid, blocksRef, setSelection]);

  return <></>;
}
